'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import * as SelectPrimitive from '@radix-ui/react-select';
import { AvoidCollisionsContext } from '@workspace/ui/components/popover';
import { ScrollArea } from '@workspace/ui/components/scroll-area';
import { cn } from '@workspace/ui/lib';
import { ChevronDown } from 'lucide-react';
import { useContext } from 'react';
import type * as React from 'react';

function Select({ ...props }: React.ComponentProps<typeof SelectPrimitive.Root>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />;
}

function SelectGroup({ ...props }: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue({ ...props }: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

function SelectTrigger({
  className,
  children,
  disabled,
  hasSelected,
  readOnly,
  chevronDownIconClassName,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  hasSelected?: boolean;
  readOnly?: boolean;
  chevronDownIconClassName?: string;
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-has-selected={hasSelected}
      data-readonly={readOnly ? 'true' : undefined}
      translate="no"
      className={cn(
        `notranslate group flex h-12 items-center justify-between gap-1 truncate rounded-2xl border px-4 py-3 text-base leading-tight font-normal focus:outline-none disabled:pointer-events-none`,
        'border-grey-300 dark:border-grey-700 disabled:text-grey-300 dark:disabled:text-grey-900 disabled:border-grey-100 dark:disabled:border-grey-900 data-[state=open]:border-neutral-200 dark:data-[state=open]:border-neutral-100',
        hasSelected
          ? 'disabled:text-grey-300 dark:disabled:text-grey-900 text-neutral-200 dark:text-neutral-100'
          : 'text-grey-700 dark:text-grey-500 disabled:text-grey-300 dark:disabled:text-grey-900',
        !disabled &&
          'focus:border-neutral-200 active:border-neutral-200 dark:focus:border-neutral-100 dark:active:border-neutral-100',
        props['aria-invalid'] && 'border-red-500 dark:border-red-400',
        readOnly && 'border-grey-300 pointer-events-none',
        className
      )}
      {...props}
    >
      {children}
      {!readOnly && (
        <SelectPrimitive.Icon asChild>
          <ChevronDown
            className={cn(
              `group-disabled:text-grey-100 group-disabled:dark:text-grey-900 h-5 w-5 text-xl text-neutral-200 transition-transform duration-200 ease-in-out group-data-[state=open]:rotate-180 dark:text-neutral-100`,
              hasSelected
                ? 'text-neutral-200 dark:text-neutral-100'
                : 'text-grey-900 dark:text-grey-100',
              chevronDownIconClassName
            )}
          />
        </SelectPrimitive.Icon>
      )}
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  scrollAreaClassName,
  className,
  children,
  position = 'popper',
  avoidCollisions,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content> & {
  scrollAreaClassName?: string;
}) {
  const contextAvoidCollisions = useContext(AvoidCollisionsContext);

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        translate="no"
        avoidCollisions={avoidCollisions ?? contextAvoidCollisions}
        className={cn(
          'notranslate border-grey-50 dark:border-grey-800 dark:bg-grey-1000 relative z-50 max-h-[320px] w-(--radix-select-trigger-width) min-w-(--radix-select-trigger-width) max-w-(--radix-select-trigger-width) origin-(--radix-select-content-transform-origin) overflow-hidden rounded-2xl border bg-neutral-100 px-3 py-4 text-neutral-200 dark:text-neutral-100',
          'shadow-[0px_17px_38px_0px_rgba(42,43,65,0.05)]',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className
        )}
        position={position}
        {...props}
      >
        <ScrollArea
          type="always"
          className={cn(
            'flex max-h-[284px] w-full flex-col gap-5 overflow-hidden px-3 py-4',
            scrollAreaClassName
          )}
        >
          <div className="flex flex-col gap-5">{children}</div>
        </ScrollArea>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn('text-muted-foreground px-2 py-1.5 text-xs', className)}
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  visualElement,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item> & {
  visualElement?: React.ReactNode;
}) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        // w-[calc(100%-1rem)] removed because the selected item was not full width
        "[&_svg:not([class*='text-'])]:text-muted-foreground hover:bg-grey-25 dark:hover:bg-grey-900 data-[state=checked]:bg-grey-25 dark:data-[state=checked]:bg-grey-900 text-grey-neutral-200 dark:text-grey-400 relative flex min-h-12 w-full cursor-default items-center gap-3 rounded-2xl px-2 py-0 text-sm font-normal outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-30 data-[state=checked]:font-medium [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className
      )}
      {...props}
    >
      {visualElement && visualElement}
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn('bg-border pointer-events-none -mx-1 my-1 h-px', className)}
      {...props}
    />
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
